import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PrimaryButton from "../buttons/PrimaryButton";

// Anonymous-visitor sticky CTA. Hidden while the AnonymousHero is on screen,
// then fades in once the user has scrolled the hero out of view. Uses an
// IntersectionObserver on the hero element so we don't pin the visibility
// to a hardcoded scroll-pixel threshold that breaks when the hero resizes.
const StickySignupCTA = ({ observeRef }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const target = observeRef?.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show the sticky CTA once the hero is no longer intersecting the
        // viewport (i.e. user has scrolled past it).
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "0px 0px -40px 0px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [observeRef]);

  return (
    <>
      {/* Hidden SVG filter — thresholds the chicken PNG's alpha channel
          so partial-opacity edge pixels (the halo from imperfect
          background removal) snap to either fully visible or fully
          transparent. Trade-off: edges become slightly more aliased,
          but the white-fringe artefact disappears. The proper fix is
          to re-export the PNG with a clean alpha cut. */}
      <svg
        width="0"
        height="0"
        aria-hidden="true"
        className="absolute overflow-hidden"
      >
        <defs>
          <filter id="chickenAlphaClip">
            <feComponentTransfer>
              <feFuncA type="discrete" tableValues="0 0 0 0 0 0 1 1 1 1 1" />
            </feComponentTransfer>
          </filter>
        </defs>
      </svg>

      <div
        aria-hidden={!visible}
        className={`
          fixed z-40
          left-3 right-3 bottom-[calc(5rem+env(safe-area-inset-bottom))]
          md:left-auto md:right-6 md:bottom-6
          flex justify-end
          transition-all duration-300 ease-out
          ${
            visible
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 translate-y-3 pointer-events-none"
          }
        `}
      >
        <div className="relative w-full md:w-auto">
          {/* Mascot peeks out from behind the CTA. The PNG already has its
              lower body cut off (transparent edge), so positioning the
              bottom of the image inside the button's vertical span hides
              the cutoff and produces a clean "peeking from behind" effect.
              DOM order puts the image before the button so the button
              naturally paints on top, no explicit z-index needed. */}
          <img
            src="/chicken-button-transparent.png"
            alt=""
            aria-hidden="true"
            draggable={false}
            className="absolute right-3 bottom-0 w-32 md:w-40 pointer-events-none select-none block"
            style={{ filter: "url(#chickenAlphaClip)" }}
          />
          <PrimaryButton
            size="medium"
            tabIndex={visible ? 0 : -1}
            onClick={() => navigate("/signup")}
            className="w-full md:w-auto"
          >
            Get my free coins
          </PrimaryButton>
        </div>
      </div>
    </>
  );
};

export default StickySignupCTA;
